import { Check, ChevronDown } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import type { SearchableComboboxOption } from "@/components/ui/searchable-combobox"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"

/** С какого числа моделей в меню появляется поиск. */
const SEARCH_FROM = 8

/**
 * Модель шота поверх модели проекта: выпадающее меню прямо у параметра, без модалки.
 * Своя модель шота подсвечена акцентом — видно, что шот отличается от проекта.
 */
export function ShotModelMenu({
  value,
  options,
  projectLabel,
  label,
  hint,
  canEdit,
  variant = "param",
  placement = "below",
  onChange,
}: {
  /** Своя модель шота; null — модель проекта. */
  value: string | null
  options: SearchableComboboxOption[]
  /** Подпись модели проекта — для пункта «Как в проекте» и для триггера без своей модели. */
  projectLabel: string
  /** Подпись триггера (модель, которой сгенерируется). */
  label: string
  hint: string
  canEdit: boolean
  /** param — тихий параметр пульта; chip — подпись в шапке шота. */
  variant?: "param" | "chip"
  placement?: "above" | "below"
  onChange: (next: string | null) => void
}) {
  const { t } = useTranslation(["shot", "workspace"])
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const overridden = value != null
  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    direction: placement === "above" ? "up" : "down",
    align: "left",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    resolveWidth: () => ({ width: "16rem" }),
    onOutsideClick: () => setOpen(false),
    onEscape: () => setOpen(false),
  })

  useEffect(() => {
    if (open) searchRef.current?.focus()
    else setQuery("")
  }, [open])

  const showSearch = options.length >= SEARCH_FROM
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options
  }, [options, query])

  const pick = (next: string | null) => {
    setOpen(false)
    if (next === value) return
    onChange(next)
  }

  const triggerText = <span className="max-w-[10rem] truncate">{label}</span>
  if (!canEdit) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center text-xs text-ui-text-secondary",
          variant === "param" && "h-7 px-2",
          overridden && "text-ui-link",
        )}
        title={label}
      >
        {triggerText}
      </span>
    )
  }

  const itemClass =
    "flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-xs transition-colors hover:bg-ui-surface-hover disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"

  const menu =
    open && menuStyle && portalRoot ? (
      <div
        ref={menuRef}
        style={menuStyle}
        role="menu"
        className="flex max-h-[60vh] flex-col overflow-y-auto rounded-lg border border-ui-border-strong p-1 shadow-xl shadow-black/50 backdrop-blur-md dark:bg-ui-bg-elevated/95 light:bg-ui-surface/95"
      >
        {showSearch ? (
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("assetShelf.modelSearch")}
            className="mb-1 h-8 w-full rounded-md bg-ui-surface-sunken px-2 text-xs text-ui-text-primary placeholder:text-ui-text-muted focus:outline-none"
          />
        ) : null}
        <button
          type="button"
          role="menuitemradio"
          aria-checked={!overridden}
          className={cn(itemClass, !overridden ? "text-ui-text-primary" : "text-ui-text-secondary")}
          onClick={() => pick(null)}
        >
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate">{t("assetShelf.modelFromProject")}</span>
            <span className="truncate text-[11px] text-ui-text-muted">{projectLabel}</span>
          </span>
          {!overridden ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
        </button>
        <div className="mx-1 my-1 h-px shrink-0 bg-ui-border" />
        {filtered.map((option) => {
          const selected = option.value === value
          return (
            <button
              key={option.value}
              type="button"
              role="menuitemradio"
              aria-checked={selected}
              disabled={option.disabled}
              title={option.disabled ? option.disabledHint : undefined}
              className={cn(itemClass, selected ? "text-ui-link" : "text-ui-text-secondary")}
              onClick={() => pick(option.value)}
            >
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {selected ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
            </button>
          )
        })}
        {filtered.length === 0 ? (
          <p className="px-2 py-2 text-xs text-ui-text-muted">{t("assetShelf.modelNothingFound")}</p>
        ) : null}
      </div>
    ) : null

  return (
    <div ref={rootRef} className="relative shrink-0">
      <ActionHintTooltip label={overridden ? t("assetShelf.modelOverriddenHint", { project: projectLabel }) : hint}>
        <button
          ref={buttonRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={label}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md text-xs transition-colors focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none",
            variant === "param" ? "h-7 px-2 hover:bg-ui-surface-hover" : "font-normal",
            overridden
              ? cn("text-ui-link", variant === "param" && "bg-ui-accent/15 hover:bg-ui-accent/25")
              : "text-ui-text-secondary hover:text-ui-text-primary",
            open && variant === "param" && "bg-ui-surface-hover",
          )}
        >
          {triggerText}
          {variant === "param" ? <ChevronDown className="h-3 w-3 shrink-0 opacity-70" /> : null}
        </button>
      </ActionHintTooltip>
      {menu && portalRoot ? createPortal(menu, portalRoot) : null}
    </div>
  )
}
